import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Minus, ShoppingCart, ArrowLeft, Clock, Leaf, X } from "lucide-react";

export default function FoodMenu() {
  const { vendorId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [menu, setMenu] = useState([]);
  const [cart, setCart] = useState({});
  const [showCart, setShowCart] = useState(false);
  const [address, setAddress] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cod");
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    api.get(`/vendors/${vendorId}`).then(r => setVendor(r.data)).catch(() => {});
    api.get(`/food/menu/${vendorId}`).then(r => setMenu(r.data)).catch(() => {});
  }, [vendorId]);

  const addToCart = (item) => setCart({ ...cart, [item.id]: { ...item, qty: (cart[item.id]?.qty || 0) + 1 } });
  const removeFromCart = (id) => {
    const next = { ...cart };
    if (!next[id]) return;
    next[id].qty -= 1;
    if (next[id].qty <= 0) delete next[id];
    setCart(next);
  };
  const cartItems = Object.values(cart);
  const subtotal = cartItems.reduce((s, x) => s + x.price * x.qty, 0);
  const deliveryFee = subtotal >= 200 ? 0 : 25;
  const platformFee = subtotal > 0 ? Math.round(subtotal * 0.05) : 0;
  const total = subtotal + deliveryFee + platformFee;

  const placeOrder = async () => {
    if (!user) { nav("/login"); return; }
    if (!address.trim()) { toast.error("Delivery address required"); return; }
    if (cartItems.length === 0) return;
    setPlacing(true);
    try {
      const items = cartItems.map(x => ({ menu_item_id: x.id, name: x.name, price: x.price, qty: x.qty }));
      await api.post("/food/orders", { vendor_id: vendorId, items, delivery_address: address, payment_method: paymentMethod });
      toast.success("Order placed! 🍽️");
      setCart({}); setShowCart(false);
      nav("/orders");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setPlacing(false); }
  };

  if (!vendor) return <div className="max-w-7xl mx-auto px-5 py-20">Loading…</div>;
  const grouped = menu.reduce((acc, m) => { (acc[m.category] = acc[m.category] || []).push(m); return acc; }, {});

  return (
    <div className="paper min-h-[80vh] pb-32" data-testid="food-menu-page">
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-8">
        <Link to="/vendors" className="inline-flex items-center gap-1 text-sm text-[#5A5A5A] mb-3 hover:underline"><ArrowLeft size={14}/> Back</Link>
        <div className="brut-card p-6 mb-6">
          <span className="premium-badge">{vendor.category}</span>
          <h1 className="font-display font-black text-3xl md:text-4xl mt-2">{vendor.business_name}</h1>
          <p className="text-sm text-[#5A5A5A] mt-1">{vendor.description}</p>
          <p className="text-xs text-[#8A8A8A] mt-2">⏱ Delivery in 30-45 min · ⭐ {vendor.rating || "—"} · 📍 {vendor.city}</p>
        </div>

        {Object.keys(grouped).length === 0 ? (
          <div className="brut-card p-12 text-center">
            <div className="font-display font-black text-2xl">Menu coming soon</div>
            <p className="text-[#5A5A5A] mt-1">This vendor hasn&apos;t published their menu yet.</p>
          </div>
        ) : Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} className="mb-8">
            <h2 className="font-display font-black text-2xl capitalize mb-3">{cat}</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {items.map(item => (
                <div key={item.id} className="brut-card p-4 flex gap-4 items-start" data-testid={`menu-item-${item.id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 border-2 border-[#0E1F18] inline-block ${item.veg ? "" : "border-[#FF7E68]"}`}>
                        <span className={`block w-1.5 h-1.5 rounded-full m-auto mt-0.5 ${item.veg ? "bg-[#25A163]" : "bg-[#FF7E68]"}`}/>
                      </span>
                      <h4 className="font-bold">{item.name}</h4>
                    </div>
                    <p className="text-sm text-[#5A5A5A] mt-1 line-clamp-2">{item.description}</p>
                    <div className="font-bold mt-2">&#8377;{item.price}</div>
                  </div>
                  {item.image && <img src={item.image} alt="" className="w-20 h-20 object-cover brut-border"/>}
                  {!cart[item.id] ? (
                    <button className="brut-btn !py-2 !px-3 !text-xs" onClick={()=>addToCart(item)} data-testid={`add-${item.id}`}><Plus size={12}/> ADD</button>
                  ) : (
                    <div className="flex items-center gap-2 brut-border bg-white px-2 py-1">
                      <button onClick={()=>removeFromCart(item.id)} aria-label="minus"><Minus size={14}/></button>
                      <span className="font-bold min-w-[16px] text-center">{cart[item.id].qty}</span>
                      <button onClick={()=>addToCart(item)} aria-label="plus"><Plus size={14}/></button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Sticky cart bar */}
      {cartItems.length > 0 && !showCart && (
        <button className="fixed bottom-6 left-1/2 -translate-x-1/2 brut-btn !bg-[#0E1F18] !text-white !px-6 !py-3 flex items-center gap-3 z-40 shadow-xl"
                onClick={()=>setShowCart(true)} data-testid="cart-cta">
          <ShoppingCart size={16}/> {cartItems.length} item{cartItems.length > 1 ? "s" : ""} · ₹{total}
          <span className="font-bold">View cart →</span>
        </button>
      )}

      {showCart && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end md:items-center justify-center p-4" onClick={()=>setShowCart(false)}>
          <div className="brut-card p-6 max-w-md w-full bg-white" onClick={(e)=>e.stopPropagation()} data-testid="cart-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-display font-black text-2xl">Your cart</h3>
              <button onClick={()=>setShowCart(false)}><X size={20}/></button>
            </div>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {cartItems.map(x => (
                <div key={x.id} className="flex justify-between items-center text-sm">
                  <div className="flex-1">{x.name} <span className="text-[#5A5A5A]">× {x.qty}</span></div>
                  <div className="font-bold">₹{x.price * x.qty}</div>
                </div>
              ))}
            </div>
            <div className="border-t-2 border-[#0E1F18] mt-4 pt-3 space-y-1 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal}</span></div>
              <div className="flex justify-between"><span>Delivery {subtotal >= 200 && <span className="text-[#25A163] text-xs">(FREE over ₹200!)</span>}</span><span>₹{deliveryFee}</span></div>
              <div className="flex justify-between"><span>Platform fee</span><span>₹{platformFee}</span></div>
              <div className="flex justify-between font-bold text-lg pt-1 border-t border-[#0E1F18]/20"><span>Total</span><span>₹{total}</span></div>
            </div>
            <div className="mt-4">
              <label className="label-eyebrow">Delivery address</label>
              <textarea className="brut-input mt-1 min-h-[60px]" value={address} onChange={(e)=>setAddress(e.target.value)} placeholder="Hostel / PG name, room number, landmark" data-testid="order-address"/>
            </div>
            <div className="mt-3">
              <label className="label-eyebrow">Payment</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button onClick={()=>setPaymentMethod("cod")} className={`brut-tag cursor-pointer ${paymentMethod === "cod" ? "lime" : "white"}`}>Cash on delivery</button>
                <button onClick={()=>setPaymentMethod("online")} className={`brut-tag cursor-pointer ${paymentMethod === "online" ? "lime" : "white"}`}>Online (later)</button>
              </div>
            </div>
            <button className="brut-btn w-full mt-5" onClick={placeOrder} disabled={placing} data-testid="place-order-btn">
              {placing ? "Placing…" : `Place order · ₹${total}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
